import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useState, type ReactNode } from "react";

type ConfirmVariant = "destructive" | "warning" | "default";

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  /** Entity-specific body shown between header and footer (e.g. info card, warnings). */
  children?: ReactNode;
  /**
   * Visual style of the confirm button.
   * `destructive` = red (default for delete / revoke actions).
   * `warning` / `default` = neutral primary.
   */
  variant?: ConfirmVariant;
  /** Text on the confirm button. Default: "Confirm". */
  confirmLabel?: string;
  /** Text on the cancel button. Default: "Cancel". */
  cancelLabel?: string;
  /** The action. Throw to keep the dialog open; resolve to close it. */
  onConfirm: () => Promise<void> | void;
}

/**
 * Standard delete-confirmation dialog: title, description, then
 * right-aligned [Cancel] [Confirm] buttons, the confirm button styled per
 * `variant`. The only sanctioned destructive-action path (window.confirm is
 * forbidden). If `onConfirm` throws, the dialog stays open so the caller can
 * surface its own error UI and let the user retry.
 */
export function ConfirmDialog({
  isOpen,
  onClose,
  title,
  description,
  children,
  variant = "destructive",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
}: ConfirmDialogProps) {
  const [isWorking, setIsWorking] = useState(false);
  const confirmColor: "error" | "primary" = variant === "destructive" ? "error" : "primary";

  const handleClose = () => {
    if (!isWorking) onClose();
  };

  const handleConfirm = async () => {
    setIsWorking(true);
    try {
      await onConfirm();
      onClose();
    } catch {
      // Caller handles error UI; keep dialog open.
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {description && <DialogContentText>{description}</DialogContentText>}
        {children}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={handleClose} disabled={isWorking}>
          {cancelLabel}
        </Button>
        <Button
          variant="contained"
          color={confirmColor}
          onClick={handleConfirm}
          disabled={isWorking}
          {...(isWorking ? { startIcon: <CircularProgress size={16} color="inherit" /> } : {})}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
